import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { CalendarIcon } from "components/icons/CalendarIcon";
import dayjs from "dayjs";
import { FormikValues, useFormikContext } from "formik";
import { formatDate } from "utils/formatDate";
import { useToast } from "hooks/useToast";

export const buildEtdMessage = (
  date: Date | string | undefined,
  currentEtd: string | undefined,
  filingName: string | undefined
): string => {
  if (!date) return "";

  const draftOf = filingName ? `a draft of ${filingName}` : "a draft";

  return currentEtd
    ? `**Quick Update on Timeline**
Your preparer now expects to share ${draftOf} by **${formatDate(
        date
      )}**, instead of **${formatDate(
        currentEtd
      )}** — we needed additional time to complete the review of your documents. Thank you for your patience; we'll let you know if anything changes.`
    : `**Expected Timeline**
Your preparer expects to share ${draftOf} for your review by **${formatDate(
        date
      )}**. We will reach out to you if anything changes.`;
};

type EtdUpdateFormProps = {
  title?: string;
  isAdding: boolean;
  currentEtd?: string;
  filingName?: string;
  hideCustomerMessage?: boolean;
};

export const EtdUpdateForm = ({
  title,
  isAdding,
  currentEtd,
  filingName,
  hideCustomerMessage,
}: EtdUpdateFormProps) => {
  const { setFieldValue, values } = useFormikContext<FormikValues>();
  const today = dayjs().startOf("day");
  const { alertToast } = useToast();
  const isUpdate = Boolean(currentEtd);
  const isUnchanged =
    isUpdate && dayjs(currentEtd).isSame(values.estimatedDeliveryTime, "day");
  const computedTitle =
    title ??
    (isUpdate
      ? "Update delivery date for this filing"
      : "Confirm delivery date for this filing");

  const handleEtdChange = (date: Date) => {
    if (dayjs(currentEtd).isSame(date, "day")) {
      alertToast({
        message: "Updated ETD is same as the current ETD",
      });
      return;
    }

    setFieldValue("estimatedDeliveryTime", date);
    setFieldValue("message", buildEtdMessage(date, currentEtd, filingName));
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title>{computedTitle || "Change ETD"}</Modal.Title>
        <Modal.Close type="button" />
      </Modal.Header>
      <Modal.Body className="t-space-y-4 t-max-h-[440px] t-overflow-y-auto">
        <DateField
          name="estimatedDeliveryTime"
          dateProps={{
            label: isUpdate
              ? "Revised Estimated Time of Delivery (ETD)"
              : "Estimated Time of Delivery (ETD)",
            required: true,
            onDateChange: (date: Date) => handleEtdChange(date),
            minDate: today.toDate(),
            rightComponent: (
              <span className="t-text-text-30 -t-mt-0.5">
                <CalendarIcon />
              </span>
            ),
          }}
        />
        {!hideCustomerMessage && (
          <TextArea
            required
            name="message"
            rows={4}
            placeholder="Enter reason for ETD change"
            onKeyDown={(e) => e.stopPropagation()}
            label="Customer-facing message"
            note={
              <span className="t-text-orange t-italic">
                This message will be sent to the customer on the task channel
              </span>
            }
          />
        )}
        <TextArea
          required={isUpdate}
          name="reason"
          rows={4}
          onKeyDown={(e) => e.stopPropagation()}
          label="Internal reason"
          note={
            <span className="t-text-text-30">
              Never shared with the customer
            </span>
          }
        />
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button type="button">Cancel</Button>
        </Modal.RawClose>
        <Button
          customType="primary"
          type="submit"
          disabled={isAdding || isUnchanged}
          isLoading={isAdding}
        >
          {isUpdate ? "Update delivery date" : "Confirm delivery date"}
        </Button>
      </Modal.FooterButtonGroup>
    </>
  );
};
